import {
  type Dispatch,
  FC,
  type SetStateAction,
  useCallback,
  useState,
} from 'react';
import { MessageSquare } from 'react-feather';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import ScrollableUncontrolledPopover from 'views/Widgets/ScrollableUncontrolledPopover';
import ThreadedCommentsModal from './ThreadedCommentsModal';
import ThreadedCommentsPreview from './ThreadedCommentsPreview';
import {
  countPubliclyVisibleComments,
  type ThreadedComment,
} from './threadedComments';

interface Props {
  contentType: number;
  objectId: number;
  title: React.ReactNode;
  addCommentLabel?: React.ReactNode;
  /** Whether the current viewer is allowed to add a comment on this object. */
  canComment?: boolean;
  /**
   * Full comment list, bulk-fetched once by the caller for every instance of
   * this button on the page. The caller is expected to hold off rendering
   * the button at all until this data has loaded (e.g. gate the whole page
   * on its bulk fetch), rather than have the button manage its own loading
   * state.
   */
  comments: ThreadedComment[];
  onCommentsChange: (comments: ThreadedComment[]) => void;
}

const ThreadedCommentButton: FC<Props> = ({
  contentType,
  objectId,
  title,
  addCommentLabel,
  canComment = false,
  comments,
  onCommentsChange,
}) => {
  const { formatMessage, formatNumber } = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Public count only: mixing in the viewer's own private comments would make
  // the badge number depend on who's looking at it. Initialized once from the
  // bulk-fetched `comments` prop (every caller gates rendering this button on
  // that fetch already having resolved), then updated ONLY via
  // onPublicCommentCountChange after a mutation - never re-derived from
  // `comments` again, since toggling one comment's privacy can change whether
  // OTHER comments (its replies) count as visible too, and the local
  // onCommentsChange patch only updates the one toggled comment. The server
  // recomputes the true count from scratch on every mutation and hands it
  // back authoritatively; re-deriving it from the locally-patched list here
  // would silently overwrite that with a stale, wrong number.
  const [publicCommentCount, setPublicCommentCount] = useState(() =>
    countPubliclyVisibleComments(comments)
  );

  const targetId = `threaded-comments-${contentType}-${objectId}`;

  const commentCountLabel = formatMessage(
    {
      id: 'app.views.widgets.comments.threaded_comment_button.count',
      defaultMessage:
        '{count, plural, =0 {No comments} one {{count} comment} other {{count} comments}}',
    },
    { count: publicCommentCount }
  );

  const toggleModal = useCallback(() => {
    setIsModalOpen((value) => !value);
  }, []);

  // ThreadedCommentsContent calls this in the setState-updater form (reading
  // the previous list to append/patch a comment), so resolve that against
  // the current `comments` prop before bubbling a plain array up to the
  // caller's own per-object state.
  const handleCommentsChange: Dispatch<SetStateAction<ThreadedComment[]>> =
    useCallback(
      (action) => {
        const resolved =
          typeof action === 'function'
            ? (action as (prev: ThreadedComment[]) => ThreadedComment[])(
                comments
              )
            : action;
        onCommentsChange(resolved);
      },
      [comments, onCommentsChange]
    );

  return (
    <>
      <Button
        type="button"
        color="link"
        size="sm"
        id={targetId}
        className="px-0 text-body fw-normal text-decoration-none d-inline-flex align-items-center gap-2 flex-shrink-0 ms-3"
        aria-label={commentCountLabel}
        onClick={toggleModal}
      >
        <span>{formatNumber(publicCommentCount)}</span>
        <MessageSquare size={14} aria-hidden="true" />
      </Button>
      {publicCommentCount > 0 && (
        <ScrollableUncontrolledPopover
          placement="left"
          trigger="hover"
          autohide={false}
          target={targetId}
          className="popover-variable-lg"
        >
          <ThreadedCommentsPreview
            comments={comments}
            contentType={contentType}
            objectId={objectId}
          />
        </ScrollableUncontrolledPopover>
      )}
      {isModalOpen && (
        <ThreadedCommentsModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          title={title}
          addCommentLabel={addCommentLabel}
          canComment={canComment}
          contentType={contentType}
          objectId={objectId}
          comments={comments}
          onCommentsChange={handleCommentsChange}
          onPublicCommentCountChange={setPublicCommentCount}
        />
      )}
    </>
  );
};

export default ThreadedCommentButton;
